import PropTypes from 'prop-types';
import {useMemo} from 'react';

import {Box, Skeleton, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';
import {IconSetSectionContext} from '@amblique/king-living/app/contentful/contexts/icon-set-section-context';
import {clampIconSetDesktopColumns} from '@amblique/king-living/app/theme/contentful/icon-set';

export const ICON_SET_APPEARANCE = {
  PRESS_LOGOS: 'CF-053',
  VALUE_PROP: 'CF-054',
};

function filterItems(items) {
  return (items ?? []).filter(
    (item) => item != null && typeof item === 'object' && item.__typename && item.sys?.id
  );
}

export function resolveIconSetAppearance(appearance) {
  const normalizedAppearance = String(appearance || '').trim().toUpperCase();

  return normalizedAppearance === ICON_SET_APPEARANCE.PRESS_LOGOS ?
    ICON_SET_APPEARANCE.PRESS_LOGOS :
    ICON_SET_APPEARANCE.VALUE_PROP;
}

/**
 * @override
 * IconSet uses iconsCollection for both supported appearances:
 * CF-053 renders a press-logo wall and CF-054 renders value-proposition cards.
 * Missing/unknown appearance defaults to CF-054.
 * explainerBlocksCollection is no longer used (to be removed from Contentful).
 *
 * @param {Object} props - Component props
 * @param {Contentful.IconSet} props.component - IconSet component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
export const IconSet = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  const icons = useMemo(
    () => filterItems(component?.iconsCollection?.items),
    [component?.iconsCollection?.items]
  );
  const appearance = resolveIconSetAppearance(component?.appearance);
  const isPressLogos = appearance === ICON_SET_APPEARANCE.PRESS_LOGOS;
  const hasItems = icons.length > 0;
  const desktopColumns = clampIconSetDesktopColumns(component?.maxItemsPerRowDesktop);
  const lastRowCount = hasItems ? (icons.length % desktopColumns || desktopColumns) : 0;
  const lastRowIsOdd = lastRowCount > 0 && lastRowCount % 2 === 1;

  const styles = useMultiStyleConfig('contentful/icon-set', {
    component,
    isComplete,
    isFetching,
    lastRowIsOdd,
    variant: isPressLogos ? 'pressLogos' : 'valueProp',
  });

  const skeletonItems = [1, 2, 3];

  if (!isComplete && isFetching) {
    return (
      <Box {...styles.root}>
        {skeletonItems.map((index) => (
          <Box key={`skeleton-${index}`} {...styles.icon}>
            <Skeleton height="64px" width="64px" />
            <Skeleton height="16px" width="80px" />
            <Skeleton height="14px" width="120px" />
          </Box>
        ))}
      </Box>
    );
  }

  if (!hasItems) {
    return null;
  }

  const rootClassName = [
    className,
    'icon-set',
    isPressLogos ? 'icon-set--logos' : 'icon-set--value-prop',
    `icon-set--${appearance.toLowerCase()}`
  ].filter(Boolean).join(' ');
  // Storefront heading: Contentful `heading` only (`title` is CMS entry label).
  const sectionHeading = component?.heading?.trim() || '';
  const sectionContext = isPressLogos ? 'icons' : 'valueProp';

  return (
    <Box as="div" {...props} className={rootClassName}>
      <IconSetSectionContext.Provider value={sectionContext}>
        <Box {...styles.wrapper} className="icon-set__wrapper">
          {sectionHeading ? (
            <Text {...styles.title} className="icon-set__title" as="h2">
              {sectionHeading}
            </Text>
          ) : null}
          <Box {...styles.root} className="icon-set__grid">
            {icons.map((item) => (
              <Box key={item.sys.id} {...styles.icon} className="icon-set__item">
                <ContentfulComponent component={item} />
              </Box>
            ))}
          </Box>
        </Box>
      </IconSetSectionContext.Provider>
    </Box>
  );
};

IconSet.displayName = 'IconSet';
IconSet.propTypes = {
  component: PropTypes.object.isRequired,
  className: PropTypes.string,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
